import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tokenStore } from '@ping/stores/token.store';

import { WSConnect } from './connection';
import { WEBSOCKET_URL_NOTIFICATION } from '@ping/configs';
import { socketConnectivityChecker } from '@ping/helpers/socket-connectivity-checker.helper';
import * as gtm from '@ping/helpers/gtm.helper';
import * as Sentry from '@sentry/react';

export enum KycNotificationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Initiated = 'Initiated',
  InitiationFailed = 'InitiationFailed',
  InitiationSubmitted = 'InitiationSubmitted',
  KycReceived = 'KycReceived',
  ValidityCheck = 'ValidityCheck',
  ValiditySucceeded = 'ValiditySucceeded',
  ValidityFailed = 'ValidityFailed',
  Confirmed = 'Confirmed',
  ConfirmFailed = 'ConfirmFailed',
  ConfirmSubmitted = 'ConfirmSubmitted',
  CallbackSucceeded = 'CallbackSucceeded',
  CallbackFailed = 'CallbackFailed',
  FinishSucceeded = 'FinishSucceeded',
  FinishFailed = 'FinishFailed',
  PingReceivedKyc = 'PingReceivedKyc',
}

export enum WithdrawNotificationStatus {
  SignatureVerified = 'SignatureVerified',
  WithdrawalCreated = 'WithdrawalCreated',
  WithdrawalFailed = 'WithdrawalFailed',
}

export enum DepositsNotificationStatus {
  Success = 'Success',
}

export enum FormANotificationStatus {
  SignatureVerified = 'SignatureVerified',
  SignatureVerificationFailed = 'SignatureVerificationFailed',
}

export enum AssetContractNotificationStatus {
  Processing = 'Processing',
  Success = 'Success',
  Failed = 'Failed',
}

/**
 * Statuses carried by `OnWalletVerificationStatusChanged`.
 *
 * The wallet webhook resolves the connector's status name straight against the shared
 * `KycTransferStatus` catalogue and publishes its display name verbatim, so `INITIATING` and
 * `CONFIRMING` arrive under those names rather than remapped onto `InitiationSubmitted` /
 * `ConfirmSubmitted` the way the KYC webhook remaps them. Both spellings are listed because the
 * legacy `verify-address-status-callback` route can still emit either.
 */
export enum WalletVerificationNotificationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Initiating = 'Initiating',
  InitiationSubmitted = 'InitiationSubmitted',
  Initiated = 'Initiated',
  InitiationFailed = 'InitiationFailed',
  ValidityCheck = 'ValidityCheck',
  ValiditySucceeded = 'ValiditySucceeded',
  ValidityFailed = 'ValidityFailed',
  Confirming = 'Confirming',
  ConfirmSubmitted = 'ConfirmSubmitted',
  Confirmed = 'Confirmed',
  ConfirmFailed = 'ConfirmFailed',
  CallbackSucceeded = 'CallbackSucceeded',
  CallbackFailed = 'CallbackFailed',
  FinishSucceeded = 'FinishSucceeded',
  FinishFailed = 'FinishFailed',
}

export enum NotificationEvents {
  OnWithdrawalStatusChanged = 'OnWithdrawalStatusChanged',
  OnKycStatusChanged = 'OnKycStatusChanged',
  OnKybStatusChanged = 'OnKybStatusChanged',
  OnOrderStatusChanged = 'OnOrderStatusChanged',
  OnFormAStatusChanged = 'OnFormAStatusChanged',
  OnAssetContractSignatureStatusChanged = 'OnAssetContractSignatureStatusChanged',
  OnWalletVerified = 'OnWalletVerified',
  OnWalletVerificationWithSignedForm = 'OnWalletVerificationWithSignedForm',
  OnWalletVerificationStatusChanged = 'OnWalletVerificationStatusChanged',
  OnGovernanceRequestStatusChanged = 'OnGovernanceRequestStatusChanged',
}

export type NotificationTypes =
  | KycNotificationStatus
  | WithdrawNotificationStatus
  | DepositsNotificationStatus
  | FormANotificationStatus
  | WalletVerificationNotificationStatus
  | NotificationEvents;

type TNotificationData = {
  status: NotificationTypes;
};

export const useNotificationWS = (callback?: any) => {
  const [notificationData, setNotificationData] = useState<TNotificationData>(null);
  const tokenQuery = `${tokenStore.getAccessToken()}`;
  const callBackEvent = useRef(null);
  const connectionRef = useRef<HubConnection>(null);

  useEffect(() => {
    if (tokenQuery === 'undefined') {
      return;
    }

    const connection = WSConnect(`${WEBSOCKET_URL_NOTIFICATION}?access_token=${tokenQuery}`);
    connectionRef.current = connection;

    connection.onclose(() => {
      gtm.webSocketDisconnectedEvent('Notifications');
      socketConnectivityChecker('Notifications', false);
      Sentry.captureMessage('[Websocket] Notifications Socket Disconnected');
    });

    connection.onreconnected(async () => {
      try {
        await handleInitialConnection();
        socketConnectivityChecker('Notifications', true);
        Sentry.captureMessage('[Websocket] Notifications Socket reconnected');
      } catch (e) {
        Sentry.captureException(e);
      }
    });

    callBackEvent.current = (event: NotificationTypes, eventCallBack) => {
      connection.on(event, message => {
        const messageData = JSON.parse(message);
        setNotificationData(messageData);
        eventCallBack?.(messageData);
        callback?.(messageData);
      });
    };

    const handleInitialConnection = async () => {
      if (connection.state === HubConnectionState.Disconnected) {
        await connection.start();
      }
      if (connection.state === HubConnectionState.Disconnecting) {
        /* For some reason onClose not works we need to check that later that's why we use setTimeout for now  */
        await new Promise(resolve => {
          /* run first time with no delay */
          const checkConnection = async () => {
            if (connection.state === HubConnectionState.Disconnected) {
              await connection.start();
            }
            connection.state === HubConnectionState.Connected ? resolve(null) : setTimeout(checkConnection, 200);
          };
          checkConnection();
        });
      }
    };

    handleInitialConnection().then(() => {
      socketConnectivityChecker('Notifications', connection.state === HubConnectionState.Connected);
    });

    return () => {
      Object.values(NotificationEvents).forEach(event => connection.off(event));
    };
  }, []);

  /*
    `notificationCallBack` is read off the ref during render, so it is null on the first render and
    a fresh subscription is registered on every render after that. This wrapper is stable and reads
    the ref at call time, so a consumer can subscribe exactly once from an effect. Effects run in
    declaration order, so the connection above already exists by the time a consumer's effect runs.
  */
  const subscribe = useCallback(
    <TMessage = unknown>(event: NotificationTypes, eventCallBack?: (message: TMessage) => void) => {
      callBackEvent.current?.(event, eventCallBack);
    },
    []
  );

  useEffect(() => {
    if (connectionRef?.current?.state) {
      socketConnectivityChecker('Notifications', connectionRef?.current?.state === HubConnectionState.Connected);
    }
  }, [connectionRef?.current?.state]);

  return { notificationData, notificationCallBack: callBackEvent.current, subscribe };
};
